import {
  Calendar,
  ChevronDown,
  DollarSign,
  Eye,
  FolderOpen,
  MousePointer,
  Percent,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsGetAssetGroupDetails,
  type GoogleAdsGetAssetGroupDetailsResult,
} from 'sprites-types/workflow/tools/googleAdsGetAssetGroupDetails'
import { ObjectDisplay } from '../shared/ObjectDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

type AssetGroupPerformanceItem = GoogleAdsGetAssetGroupDetailsResult[number]

// Utility functions
const formatCurrency = (amount: number | null | undefined) => {
  if (amount == null) return 'N/A'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

const formatNumber = (num: number | null | undefined) => {
  if (num == null) return 'N/A'
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

const formatPercent = (value: number | null | undefined) => {
  if (value == null) return 'N/A'
  return `${value.toFixed(2)}%`
}

const getStatusColor = (status: string | null | undefined) => {
  switch (status) {
    case 'ENABLED':
      return 'bg-green-100 text-green-800'
    case 'PAUSED':
      return 'bg-yellow-100 text-yellow-800'
    case 'REMOVED':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getAdStrengthColor = (strength: string | null | undefined) => {
  switch (strength) {
    case '1':
    case '2':
      return 'bg-red-100 text-red-800'
    case '3':
      return 'bg-yellow-100 text-yellow-800'
    case '4':
    case '5':
      return 'bg-green-100 text-green-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function AssetGroupItem({ assetGroup }: { assetGroup: AssetGroupPerformanceItem }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const perf = assetGroup.performance || {}

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-gray-500" />
            <h4 className="font-medium text-gray-900">
              {assetGroup.assetGroupName || `Asset Group ${assetGroup.assetGroupId}`}
            </h4>
            {assetGroup.status && (
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(assetGroup.status)}`}
              >
                {assetGroup.status}
              </span>
            )}
            {assetGroup.adStrength && (
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${getAdStrengthColor(assetGroup.adStrength)}`}
              >
                Ad Strength: {assetGroup.adStrength}
              </span>
            )}
          </div>
          {assetGroup.campaignName && (
            <p className="text-sm text-gray-600">Campaign: {assetGroup.campaignName}</p>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="ml-2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown
            className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Performance Metrics Grid */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
          <div className="mb-1 flex items-center gap-1">
            <Eye className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Impressions</span>
          </div>
          <div className="text-base font-bold text-gray-900">{formatNumber(perf.impressions)}</div>
        </div>

        {perf.clicks != null && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
            <div className="mb-1 flex items-center gap-1">
              <MousePointer className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Clicks</span>
            </div>
            <div className="text-base font-bold text-gray-900">{formatNumber(perf.clicks)}</div>
          </div>
        )}

        {perf.cost != null && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
            <div className="mb-1 flex items-center gap-1">
              <DollarSign className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Cost</span>
            </div>
            <div className="text-base font-bold text-gray-900">{formatCurrency(perf.cost)}</div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <div className="grid grid-cols-2 gap-3">
            {perf.ctr != null && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">CTR: </span>
                <span className="text-gray-900">{formatPercent(perf.ctr)}</span>
              </div>
            )}
            {perf.cpc != null && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">CPC: </span>
                <span className="text-gray-900">{formatCurrency(perf.cpc)}</span>
              </div>
            )}
            {perf.conversions != null && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">Conversions: </span>
                <span className="text-gray-900">{formatNumber(perf.conversions)}</span>
              </div>
            )}
            {perf.conversionRate != null && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">Conversion Rate: </span>
                <span className="text-gray-900">{formatPercent(perf.conversionRate)}</span>
              </div>
            )}
          </div>

          {/* Daily Breakdown */}
          {assetGroup.dailyBreakdown && assetGroup.dailyBreakdown.length > 0 && (
            <div className="mt-3">
              <div className="mb-2 flex items-center gap-1">
                <Calendar className="h-3 w-3 text-indigo-500" />
                <span className="text-xs font-medium text-gray-700">Daily Performance</span>
              </div>
              <div className="space-y-1">
                {assetGroup.dailyBreakdown.slice(-7).map((day, index) => (
                  <div key={day.date || index} className="flex justify-between text-xs">
                    <span className="text-gray-600">{day.date}</span>
                    <div className="flex gap-3 text-gray-900">
                      <span>{formatNumber(day.impressions)} imp</span>
                      {day.clicks != null && <span>{formatNumber(day.clicks)} clicks</span>}
                      {day.cost != null && <span>{formatCurrency(day.cost)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function GoogleAdsGetAssetGroupDetailsDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const [showJson, setShowJson] = useState(false)
  const schema = getToolResultSchema(googleAdsGetAssetGroupDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsGetAssetGroupDetailsResult

  if (showJson) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Asset Group Details Results</h3>
          <button
            onClick={() => setShowJson(false)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            View Formatted
          </button>
        </div>
        <ObjectDisplay data={result} />
      </div>
    )
  }

  if (!result || result.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Asset Group Details Results</h3>
        <p className="mt-2 text-sm text-gray-600">No asset group performance data found.</p>
      </div>
    )
  }

  // Calculate totals
  const totals = result.reduce(
    (acc, assetGroup) => {
      const perf = assetGroup.performance || {}
      acc.impressions += perf.impressions || 0
      acc.clicks += perf.clicks || 0
      acc.cost += perf.cost || 0
      acc.conversions += perf.conversions || 0
      return acc
    },
    { impressions: 0, clicks: 0, cost: 0, conversions: 0 }
  )

  const avgCTR = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Asset Group Details Results</h3>
          <p className="text-xs text-gray-600">
            {result.length} asset group{result.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowJson(true)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          View JSON
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="mb-1 flex items-center gap-1">
            <Eye className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Total Impressions</span>
          </div>
          <div className="text-lg font-bold text-gray-900">{formatNumber(totals.impressions)}</div>
        </div>
        {totals.clicks > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <MousePointer className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Total Clicks</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{formatNumber(totals.clicks)}</div>
          </div>
        )}
        {totals.cost > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <DollarSign className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Total Cost</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{formatCurrency(totals.cost)}</div>
          </div>
        )}
        {avgCTR > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <Percent className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Avg CTR</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{formatPercent(avgCTR)}</div>
          </div>
        )}
      </div>

      {/* Asset Groups List */}
      <div className="space-y-3">
        {result.map((assetGroup, index) => (
          <AssetGroupItem key={assetGroup.assetGroupId || index} assetGroup={assetGroup} />
        ))}
      </div>
    </div>
  )
}

async function downloadGoogleAdsAssetGroupPerformance(
  _nodeExecution: NodeExecution
): Promise<void> {
  return Promise.resolve()
}

function googleAdsGetAssetGroupDetailsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsGetAssetGroupDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  return `## Google Ads Asset Group Details\n\n\`\`\`json\n${JSON.stringify(result, null, 2)}\n\`\`\`\n\n---`
}

export const GoogleAdsGetAssetGroupDetailsDisplay = createWorkflowToolComponent(
  GoogleAdsGetAssetGroupDetailsDisplayComponent,
  downloadGoogleAdsAssetGroupPerformance,
  googleAdsGetAssetGroupDetailsToMarkdown
)
